import { CaptureStep } from "@idw-types/data/capture-step";
import { ChangeEvent, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import BrowserImageCompression from "browser-image-compression";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { Logger } from "@utils/logger";
import { PrimaryButton } from "../primary-button/primary-button";
import { getCaptureStepNativeCameraButtonText } from "@utils/capture-step-util";
import { AuditLogTypes } from "@constants/audit-log-types";
import styles from "./native-capture-container.less";

type NativeCaptureContainerProps = {
    captureStep: CaptureStep;
    isCapturing: boolean;
    isProcessing: boolean;
    setIsCapturingAndProcessing: (isCapturing: boolean, isProcessing: boolean) => void;
    setImageUrl: (imageUrl: string) => void;
};

export const NativeCaptureContainer = (props: NativeCaptureContainerProps) => {
    const {
        captureStep,

        isCapturing,
        isProcessing,
        setIsCapturingAndProcessing,
        setImageUrl
    } = props;

    const { writeAuditLog } = useAuditLogger();
    const content = useSelector(getLanguageContent);
    const inputRef = useRef<HTMLInputElement>(null);

    const buttonText = useMemo(() => {
        return getCaptureStepNativeCameraButtonText(captureStep.type, content);
    }, [captureStep.type, content]);


    const onClickCapture = () => {
        writeAuditLog(AuditLogTypes.DCUI_CAPTURE_BUTTON_HIT);
        if (inputRef.current) {
            Logger.info(`[NativeCameraComponent] ******* CAPTURE PHASE ***********`);
            Logger.info(`[NativeCameraComponent] User clicked on the Capture button`);
            setIsCapturingAndProcessing(true, false);
            inputRef.current.value = null;
            inputRef.current.click();
        }
    };

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.length > 0) {
            Logger.info(`[NativeCameraComponent] The image was captured`);
            setIsCapturingAndProcessing(false, true);
            Logger.info(`[NativeCameraComponent] ******* IMAGE COMPRESSION PHASE ***********`);
            Logger.info(`[NativeCameraComponent] Original file instance of Blob: ${event.target.files[0] instanceof Blob}`);
            Logger.info(`[NativeCameraComponent] Original file size: ${event.target.files[0].size / 1024 / 1024} MB`);

            try {
                const imageFileDataURL = await BrowserImageCompression.getDataUrlFromFile(event.target.files[0]);

                setImageUrl(imageFileDataURL);
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_CAPTURED);
                setIsCapturingAndProcessing(false, false);
            } catch (error) {
                Logger.error(`[NativeCameraComponent] Image compression error: `, error);
            }
        }
    };

    return (
        <div className={styles.nativeCaptureContainer}>
            <input
                ref={inputRef}
                className={styles.fileInput}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleChange}
                id={"capture-input"}
            />
            {!isCapturing && !isProcessing && (
                <div className={styles.buttonContainer}>
                    <PrimaryButton
                        disabled={false}
                        label={buttonText}
                        type={"button"}
                        automationId={"start-capture"}
                        id={"start-capture"}
                        hideSpinner={true}
                        onClick={onClickCapture}
                    />
                </div>
            )}
        </div>
    );
};